import { gql, useMutation } from '@apollo/client'
import { LoadingButton } from '@mui/lab'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { Mutation } from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { enqueueSnackbar } from 'notistack'
import { useMemo, useState } from 'react'

import { invalidateApolloQuery } from '@src/config/ApolloClient'

const InviteUsersDialog: React.FC<{
  open: boolean
  onClose: () => void

  invitationName: string
  invitationAccessibleType: string
  invitationAccessibleId: string
  invitationSenderUserId: string
}> = (props) => {
  const [emails, setEmails] = useState<string>('')
  const parsedEmails = useMemo(() => {
    return emails
      .split(/[\s,]+/)
      .filter((x) => x.split('@').length === 2)
      .filter(Boolean)
  }, [emails])

  const [createAccessInvitation] = useMutation<Mutation>(
    CREATE_ACCESS_INVITATION,
  )

  const [isSubmitting, setIsSubmitting] = useState(false)

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      TransitionProps={{
        onExited: () => {
          setEmails('')
        },
      }}
    >
      <DialogTitle>
        {I18n.t('retailerSettings.users.inviteUsers.title')}
      </DialogTitle>

      <DialogContent>
        <TextField
          minRows={3}
          multiline
          fullWidth
          label={I18n.t(
            'retailerSettings.users.inviteUsers.fields.email.label',
          )}
          helperText={I18n.t(
            'retailerSettings.users.inviteUsers.fields.email.helperText',
          )}
          autoFocus
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          disabled={parsedEmails.length === 0}
          loading={isSubmitting}
          onClick={async () => {
            setIsSubmitting(true)

            try {
              await Promise.all(
                parsedEmails.map((x) => {
                  return createAccessInvitation({
                    variables: {
                      name: props.invitationName,
                      accessible_type: props.invitationAccessibleType,
                      accessible_id: props.invitationAccessibleId,
                      recipient_email_address: x,
                      sender_user_id: props.invitationSenderUserId,
                    },
                  }).catch((err) => {
                    console.error(err)
                  })
                }),
              )

              enqueueSnackbar(
                I18n.t(
                  'retailerSettings.users.inviteUsers.notifications.invitedUsersSuccess',
                ),
                { variant: 'success' },
              )

              invalidateApolloQuery([
                'getAccessInvitations',
                'getAccessInvitationsByRetailerIds',
              ])

              props.onClose()
            } catch (err) {
              console.error(err)
              enqueueSnackbar(
                I18n.t(
                  'retailerSettings.users.inviteUsers.notifications.invitedUsersError',
                ),
                { variant: 'error' },
              )
            }
            setIsSubmitting(false)
          }}
        >
          {I18n.t('retailerSettings.users.inviteUsers.submitButtonLabel', {
            count: parsedEmails.length,
          })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const CREATE_ACCESS_INVITATION = gql`
  mutation createAccessInvitationInviteUsersDialog(
    $name: String!
    $accessible_type: String!
    $accessible_id: ID!
    $recipient_email_address: String!
    $sender_user_id: ID!
  ) {
    createAccessInvitation(
      params: {
        access_invitation: {
          name: $name
          accessible_type: $accessible_type
          accessible_id: $accessible_id
          recipient_email_address: $recipient_email_address
          sender_user_id: $sender_user_id
        }
      }
    ) {
      id
    }
  }
`

export default InviteUsersDialog
